import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AgGridModule } from 'ag-grid-angular';
import {
    AgGridEvent,
    BodyScrollEvent,
    CellClickedEvent,
    ColumnResizedEvent,
    FilterChangedEvent,
    GridApi,
    GridOptions,
    GridReadyEvent,
    SortChangedEvent,
} from 'ag-grid-community';
import { ModalModule } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject } from 'rxjs';
import { AgGridExtensionsModule } from '../agGridExtensions.module';
import {
    AgGridExportOptions,
    AgGridLayoutSpecificationModel,
    AgGridToolPanelAction,
    AgGridToolPanelButton,
} from '../ToolPanel/models';
import { BulkEditButtonOptions } from '../ToolPanel/agGridToolPanel.component';

@Component({
    selector: 'ws-ag-grid-angular',
    templateUrl: './wsAgGridWrapper.component.html',
    styles: [':host { height: 100%; width: 100%; overflow: auto }'],
    imports: [CommonModule, AgGridExtensionsModule, AgGridModule, ModalModule],
})
export class WsAgGridWrapperComponent implements OnInit {
    constructor(private _toastr: ToastrService) {}

    // Ag-Grid
    @Input() set gridOptions(gridOptions: GridOptions) {
        gridOptions.processUnpinnedColumns = (params) => {
            if (params.columns.length) {
                this._toastr.warning(
                    `${params.columns.length} column${params.columns.length > 1 ? 's were' : ' was'} unpinned to keep the grid accessible.`,
                    'Too many pinned columns',
                );
            }
            return params.columns;
        };
        this._gridOptions = gridOptions;
    }

    @Input() rowSelection: 'single' | 'multiple';
    @Input() gridClass: string;
    @Input() wrapperClass: string;
    @Input() pinnedTopRowData: any[];

    // Tool panel
    @Input() companyId: number;
    @Input() gridId: string | System.Guid;
    @Input() isBulkUpdateVisible$: BehaviorSubject<boolean | BulkEditButtonOptions>;
    @Input() isBulkDeleteVisible$: BehaviorSubject<boolean>;
    @Input() isCustomButtonVisible$: BehaviorSubject<boolean>;
    @Input() exportOptions: AgGridExportOptions;
    @Input() showConfigureLayout: boolean = true;
    @Input() showFilter: boolean = true;
    @Input() showResizeColumns: boolean = true;
    @Input() defaultSizeToFit: boolean = true;
    @Input() defaultSizeToContent: boolean = false;
    @Input() defaultColorizeHeaders: boolean = false;
    @Input() exportIcon: string = 'fa-file-excel-o';
    @Input() exportHelpContentId: string = 'ag-grid-tool-panel.export';
    @Input() additionalActions: AgGridToolPanelAction[];
    @Input() additionalButtons: AgGridToolPanelButton[];
    @Input() forceShowToolPanel: boolean = false;
    @Input() overlayNoRowsTemplate: string;

    // Ag-Grid outputs
    @Output() gridReady: EventEmitter<GridReadyEvent> = new EventEmitter<GridReadyEvent>();
    @Output() filterChanged: EventEmitter<FilterChangedEvent> = new EventEmitter<FilterChangedEvent>();
    @Output() selectionChanged: EventEmitter<AgGridEvent> = new EventEmitter<AgGridEvent>();
    @Output() cellClicked: EventEmitter<CellClickedEvent> = new EventEmitter<CellClickedEvent>();
    @Output() columnResized: EventEmitter<ColumnResizedEvent> = new EventEmitter<ColumnResizedEvent>();
    @Output() bodyScroll: EventEmitter<BodyScrollEvent> = new EventEmitter<BodyScrollEvent>();
    @Output() sortChanged: EventEmitter<SortChangedEvent> = new EventEmitter<SortChangedEvent>();

    // Tool panel outputs
    @Output() bulkUpdate: EventEmitter<void> = new EventEmitter<void>();
    @Output() bulkDelete: EventEmitter<void> = new EventEmitter<void>();
    @Output() customButtonClick: EventEmitter<void> = new EventEmitter<void>();
    @Output() filtersEnabled = new EventEmitter<boolean>();
    @Output() sizeColumnsToFit = new EventEmitter<void>();
    @Output() sizeColumnsToContent = new EventEmitter<void>();
    @Output() layoutChanged = new EventEmitter<AgGridLayoutSpecificationModel>();
    @Output() firstDataRendered = new EventEmitter<AgGridEvent>();

    gridApi: GridApi;
    agGridReady: boolean;
    hasToolbar: boolean;

    private _gridOptions: GridOptions;
    private _gridReadyEvent: GridReadyEvent;

    get gridOptions(): GridOptions {
        return this._gridOptions;
    }

    ngOnInit(): void {
        this.hasToolbar = !!(this.companyId || this.gridId || this.forceShowToolPanel);
    }

    toolPanelReady(): void {
        this.gridReady.emit(this._gridReadyEvent);
    }

    onAgGridReady(event: GridReadyEvent): void {
        this._gridReadyEvent = event;
        this.gridApi = event.api;
        if (this.hasToolbar) {
            this.agGridReady = true;
            // The layout specification (which signals readiness via toolPanelReady) is only shown
            // when configuring layout and a gridId is present. Otherwise, signal readiness directly.
            if (!this.showConfigureLayout || !this.gridId) {
                this.toolPanelReady();
            }
            return;
        }
        this.gridReady.emit(this._gridReadyEvent);
    }
}
